import { Box } from '@mui/material';
import { DocumentStatus, Locale, Media } from '@synergy/models';
import { Field, FieldProps, Form, Formik } from 'formik';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { PaloJumpBar, PaloRecordCard } from '@synergy/components-palo';
import { PoolMarkdownEditor, ScrollToFieldError } from '@synergy/components';
import { ReactNode } from 'react';
import { connect } from 'react-redux';
import { customerMediaSelector, priceMatrixMediaSelector } from '@synergy/data-access';
import { postProcessMarkdown } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import urljoin from 'url-join';

import { AccountPurchase } from '../models/account-purchase';
import { AppState } from '../../../store/models/app-state';
import {
    FormControlledStepComponent,
    FormControlledStepComponentProps,
} from '../../../common/components/abstract/form-controlled-step-component';
import { Inquiry } from '../models/inquiry';
import { InquiryDetailOffersTable } from './inquiry-detail-offers-table';
import { Offer } from '../../../common/models/offer';
import { OfferStatus } from '../../../common/models/enums/offer-status';
import { PaloDirtyComparison } from '../../../common/components/palo/palo-dirty-context';
import {
    addOfferToAccountPurchase,
    declineOffer,
    sendPriceComparison,
    updateOfferOfInquiry,
    updateOfferRecommendation,
} from '../../../common/actions/offer-actions';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import { customerChipLabel, customerLabel } from '../utils/customer-label';
import { getPriceCalculation } from '../utils/get-price-calculation';
import { pickCommonPartial } from '../../../common/utils/pick-common-partial';

const c = tokens.color;

/** Two lines to start with, room for about twelve (the rule for every remark, GF 14.10.2026). */
const REMARK_MIN_HEIGHT = 56;
const REMARK_MAX_HEIGHT = 264;

/**
 * The price infos sent for one delivery address, latest first. The backend
 * names the stored PDF after the address, ending in `_<id>-NN.pdf`
 * (PriceMatrixPdf::getPriceMatrixPdfName), which is what ties a file to it.
 */
export const sentPriceInfosOf = (priceMatrixMedia: Media[], accountPurchaseId?: number): Media[] => {
    if (!accountPurchaseId) {
        return [];
    }
    const pattern = new RegExp(`_${accountPurchaseId}-\\d{2}\\.pdf$`);

    return priceMatrixMedia
        .filter(media => pattern.test(media.originalName ?? ''))
        .sort((left, right) => new Date(right.createdAt ?? 0).getTime() - new Date(left.createdAt ?? 0).getTime());
};

interface InquiryDetailOffersProps extends FormControlledStepComponentProps<Partial<Inquiry>> {
    currency: string;
    locale: Locale;
    mediaCustomer: Media[];
    mediaPriceMatrix: Media[];
    mediaBaseUrl: string;
    declineOffer: (offerId: number, subject: string, message: string) => void;
    updateOfferRecommendation: (newRecommendedOfferId: number, isRecommended: boolean) => void;
    addOfferToAccountPurchase: (accountPurchaseId: number, offer: Partial<Offer>) => void;
    updateOfferOfInquiry: (offer: Partial<Offer>, newFiles: File[], mediaToRemove: Media[]) => void;
    sendPriceComparison: (
        inquiryId: number,
        accountPurchaseId: number,
        supplierAccountIds: number[],
        allDeliveryAddresses: boolean,
        supplierAccountIdsByAddress?: Record<number, number[]>
    ) => void;
}

interface InquiryDetailOffersState {
    /**
     * Selected offers per delivery address. All addresses are on screen at
     * once, so one shared list would let a price comparison span two of them
     * (#2185). Keyed by the address index.
     */
    selectedItems: Record<number, number[]>;
    /** Cards opened or closed by hand; the rest follow the count (see isCardOpen). */
    openCards: Record<number, boolean>;
}

interface InquiryDetailOffersFormValues {
    noteToCustomer?: string;
}

/**
 * "Angebote & Preisspiegel": one card per delivery address, the way the
 * customers stand on the overview (#2252). Each says in its head whether the
 * price info is out, holds the offers with what was ordered from them, and
 * has one main action. The remark to the customer stands under the cards,
 * open, and the files that go with the price info moved into the rail (#2256).
 */
class InquiryDetailOffersBase extends FormControlledStepComponent<
    Partial<Inquiry>,
    InquiryDetailOffersFormValues,
    InquiryDetailOffersProps,
    InquiryDetailOffersState
> {
    public state: InquiryDetailOffersState = {
        selectedItems: {},
        openCards: {},
    };

    public initialValues: InquiryDetailOffersFormValues = {
        noteToCustomer: '',
    };

    /**
     * The shape this step hands to the wizard. Also what the unsaved marker
     * compares in, on both sides — the post-processing below would otherwise
     * read as a change on its own.
     */
    private toHandover = (values: InquiryDetailOffersFormValues): Partial<Inquiry> => ({
        ...pickCommonPartial(this.initialValues, values),
        noteToCustomer: postProcessMarkdown(values.noteToCustomer),
    });

    protected onSubmit = (values: InquiryDetailOffersFormValues) => {
        this.props.propagateInternalState(this.toHandover(values));
    };

    private handleOfferSelectionChange = (index: number) => (selectedOffers: Offer[]) => {
        this.setState(state => ({
            selectedItems: { ...state.selectedItems, [index]: selectedOffers.map(offer => offer.id) },
        }));
    };

    private handleOfferChanged = (offer: Partial<Offer>, newFiles: File[], mediaToRemove: Media[]) => {
        if (offer) {
            this.props.updateOfferOfInquiry(offer, newFiles, mediaToRemove);
        }
    };

    private handleOfferCreated = (accountPurchase: AccountPurchase) => (offer: Partial<Offer>) => {
        if (offer && accountPurchase && accountPurchase.id) {
            this.props.addOfferToAccountPurchase(accountPurchase.id, offer);
        }
    };

    private toFormValues(state?: Partial<Inquiry>): InquiryDetailOffersFormValues {
        // Allow all properties of the inquiry partial, unused ones are ignored
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const definedState: any = {};

        Object.keys(state ?? {})
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter(key => !!(state as any)[key])
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .forEach(key => (definedState[key] = (state as any)[key]));

        // Reduced to the fields of this step, the same way onSubmit hands them
        // back. Otherwise the document's extra fields would make every
        // comparison against it fail.
        return { ...this.initialValues, ...pickCommonPartial(this.initialValues, definedState) };
    }

    private updateInternalValuesByProps(props: InquiryDetailOffersProps) {
        this.formikInstance?.setValues(this.toFormValues(props.internalState));
    }

    private handleSendPriceComparison = (
        accountPurchaseId: number,
        supplierAccounts: number[],
        allDeliveryAddresses: boolean,
        supplierAccountIdsByAddress?: Record<number, number[]>
    ) => {
        const id = this.props.globalState?.id;
        if (id) {
            this.props.sendPriceComparison(
                id,
                accountPurchaseId,
                supplierAccounts,
                allDeliveryAddresses,
                supplierAccountIdsByAddress
            );
        }
    };

    /** From three addresses on, the cards start closed but the last one. */
    private isCardOpen(index: number, count: number): boolean {
        return this.state.openCards[index] ?? (count <= 2 || index === count - 1);
    }

    private toggleCard = (index: number, count: number) => () =>
        this.setState(state => ({ openCards: { ...state.openCards, [index]: !this.isCardOpen(index, count) } }));

    private jumpToCard = (index: number) => () => {
        this.setState(state => ({ openCards: { ...state.openCards, [index]: true } }));
        requestAnimationFrame(() =>
            document.getElementById(`inquiry-offers-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        );
    };

    /** Whether the price info is out for this address, with the PDF that went. */
    private renderSentState(purchase: AccountPurchase): ReactNode {
        const latest = sentPriceInfosOf(this.props.mediaPriceMatrix, purchase.id)[0];

        if (!latest) {
            return (
                <Box component="span" sx={{ fontSize: '12px', color: c.warnInk, whiteSpace: 'nowrap' }}>
                    {'● '}
                    <FormattedMessage id="app.inquiries.detail.priceInfo.notSent" />
                </Box>
            );
        }

        return (
            <Box component="span" sx={{ fontSize: '12px', color: c.okInk, whiteSpace: 'nowrap' }}>
                {'✓ '}
                <FormattedMessage id="app.inquiries.detail.priceInfo.sent" />
                {latest.createdAt && (
                    <>
                        {' '}
                        <FormattedDate value={latest.createdAt} day="2-digit" month="2-digit" />{' '}
                        <FormattedTime value={latest.createdAt} />
                    </>
                )}
                {' · '}
                <a
                    href={urljoin(this.props.mediaBaseUrl, latest.pathname)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: c.accentHi }}
                    onClick={event => event.stopPropagation()}
                >
                    PDF
                </a>
            </Box>
        );
    }

    public componentDidMount() {
        this.props.onCtaButtonChanged?.(false, undefined);
        this.updateInternalValuesByProps(this.props);
    }

    public UNSAFE_componentWillReceiveProps(newProps: InquiryDetailOffersProps) {
        if (newProps.internalState !== this.props.internalState) {
            this.updateInternalValuesByProps(newProps);
        }
    }

    public render() {
        const { locale, currency, mediaCustomer, mediaPriceMatrix, unlocked } = this.props;
        const { selectedItems } = this.state;

        const readonly =
            (!unlocked &&
                this.props.globalState?.status &&
                DocumentStatus.Created !== this.props.globalState.status &&
                DocumentStatus.Ongoing !== this.props.globalState.status) ||
            false;

        const accountPurchases = this.props.globalState?.accountPurchases ?? [];
        const supplierAccounts = this.props.globalState?.supplierAccounts ?? [];
        const customerCount = new Set(accountPurchases.map(purchase => purchase.customerAccount?.id)).size;
        const count = accountPurchases.length;

        return (
            <Box>
                {count > 1 && (
                    <PaloJumpBar
                        title={<FormattedMessage id="app.inquiries.detail.customers" />}
                        items={accountPurchases.map((purchase, index) => {
                            const offers = (purchase.offers ?? []).filter(
                                offer => OfferStatus.Created !== offer.status
                            ).length;
                            const sent = sentPriceInfosOf(mediaPriceMatrix, purchase.id).length > 0;

                            return {
                                key: index,
                                label: customerChipLabel(purchase, accountPurchases),
                                meta: (
                                    <FormattedMessage
                                        id={
                                            sent
                                                ? 'app.inquiries.detail.priceInfo.barSent'
                                                : 'app.inquiries.detail.priceInfo.barOpen'
                                        }
                                        values={{ count: offers }}
                                    />
                                ),
                                onClick: this.jumpToCard(index),
                            };
                        })}
                    />
                )}

                {/* Every delivery address with its own offers, one under the
                    other. The drop-down above a single table hid how many
                    addresses an inquiry had (#2185). */}
                {accountPurchases.map((purchase, index) => {
                    const calculation = getPriceCalculation(purchase.offers);
                    const address = purchase.deliveryAddress;

                    return (
                        <PaloRecordCard
                            key={purchase.id ?? index}
                            id={`inquiry-offers-${index}`}
                            title={
                                <>
                                    <FormattedMessage id="app.header.customerWithValue" values={{ value: index + 1 }} />
                                    {customerLabel(purchase) ? ` · ${customerLabel(purchase)}` : ''}
                                </>
                            }
                            meta={[
                                address && [address.street, address.number].filter(Boolean).join(' '),
                                address?.city,
                                purchase.contact?.fullName,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                            actions={this.renderSentState(purchase)}
                            open={this.isCardOpen(index, count)}
                            onToggle={this.toggleCard(index, count)}
                        >
                            <InquiryDetailOffersTable
                                offers={purchase.offers || []}
                                suppliers={supplierAccounts.map(supplierAccount => supplierAccount.account)}
                                locale={locale}
                                currency={currency}
                                selectedOffers={selectedItems[index] ?? []}
                                recommendedOfferId={calculation.recommendationId}
                                bestOfferId={calculation.bestOfferId}
                                accountPurchase={purchase}
                                // What is ticked on each of the member's other
                                // addresses goes out for that address (R-223).
                                memberSelections={accountPurchases
                                    .map((other, otherIndex) => ({
                                        accountPurchase: other,
                                        offers: (other.offers ?? []).filter(offer =>
                                            (selectedItems[otherIndex] ?? []).includes(offer.id)
                                        ),
                                    }))
                                    .filter(
                                        ({ accountPurchase: other }) =>
                                            other !== purchase &&
                                            other.customerAccount?.id === purchase.customerAccount?.id
                                    )}
                                customerMedia={mediaCustomer}
                                customerCount={customerCount}
                                readonly={!!readonly}
                                globalState={this.props.globalState}
                                onSelectionChanged={this.handleOfferSelectionChange(index)}
                                onRecommendationChanged={this.props.updateOfferRecommendation}
                                onOfferDecline={this.props.declineOffer}
                                onOfferChanged={this.handleOfferChanged}
                                onOfferCreated={this.handleOfferCreated(purchase)}
                                onSendPriceComparison={this.handleSendPriceComparison}
                            />
                        </PaloRecordCard>
                    );
                })}

                <Formik
                    initialValues={this.initialValues}
                    onSubmit={this.onSubmit}
                    isInitialValid={true}
                    innerRef={ref => (this.formikInstance = ref)}
                >
                    {() => (
                        <Form id="note">
                            <PaloDirtyComparison
                                baseline={this.toHandover(this.toFormValues(this.props.documentState))}
                                normalize={this.toHandover}
                            />
                            <ScrollToFieldError />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '8px',
                                    mt: '16px',
                                    mb: '5px',
                                    fontSize: '12px',
                                    color: c.ink2,
                                }}
                            >
                                <FormattedMessage id="app.header.noteToCustomer" />
                                <Box component="span" sx={{ color: c.ink3 }}>
                                    <FormattedMessage id="app.inquiries.detail.noteToCustomerGoesWith" />
                                </Box>
                                <Box component="span" sx={{ marginLeft: 'auto', color: c.ink3 }}>
                                    <FormattedMessage id="app.inquiries.detail.noteToCustomerFiles" />
                                </Box>
                            </Box>
                            <Field name="noteToCustomer">
                                {({ field, form }: FieldProps<string>) => (
                                    <PoolMarkdownEditor
                                        name={field.name}
                                        value={field.value ?? ''}
                                        minHeight={REMARK_MIN_HEIGHT}
                                        maxHeight={REMARK_MAX_HEIGHT}
                                        onChange={value => form.setFieldValue(field.name, value)}
                                        readOnly={readonly}
                                    />
                                )}
                            </Field>
                        </Form>
                    )}
                </Formik>
            </Box>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    currency: state.settings.currency,
    locale: bestLocaleSelector(state),
    mediaCustomer: customerMediaSelector(state),
    mediaPriceMatrix: priceMatrixMediaSelector(state),
    mediaBaseUrl: state.settings.mediaBaseUrl,
});

export const InquiryDetailOffers = connect(mapStateToProps, {
    updateOfferRecommendation,
    declineOffer,
    addOfferToAccountPurchase,
    updateOfferOfInquiry,
    sendPriceComparison,
})(InquiryDetailOffersBase);
